import { useEffect, useState } from 'react';
import type { EventReportView, StateView } from '@quiz/shared';
import type { PlayerSession } from '../lib/session.js';
import { api } from '../lib/api.js';
import { Button, Card, Leaderboard, SupportCta } from '../components/ui.js';

export function EndScreen({
  session,
  state,
  onLeave,
}: {
  session: PlayerSession;
  state: StateView;
  onLeave: () => void;
}) {
  const [mine, setMine] = useState<EventReportView['players'][number] | null>(null);

  useEffect(() => {
    // The player's own breakdown. Everything here is already theirs to see.
    api<{ you: EventReportView['players'][number] }>('/api/me/summary', {
      token: session.playerToken,
    })
      .then((r) => setMine(r.you))
      .catch(() => setMine(null));
  }, [session.playerToken]);

  const winner = state.leaderboard?.[0];
  const youWon = winner?.playerId === session.playerId;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <Card className="text-center">
        <p className="text-xs uppercase tracking-wide text-muted">That&rsquo;s the quiz</p>
        <p className="mt-2 text-5xl font-bold tabular-nums">{state.you.score}</p>
        <p className="text-sm text-muted">
          {youWon ? 'and you won it' : `finished ${ordinal(state.you.rank)}`}
        </p>
      </Card>

      {winner && !youWon && (
        <Card className="text-center">
          <p className="text-xs uppercase tracking-wide text-muted">Tonight&rsquo;s winner</p>
          <p className="mt-1 text-2xl font-bold text-flag">{winner.callSign}</p>
        </Card>
      )}

      {state.leaderboard && (
        <Leaderboard
          rows={state.leaderboard}
          highlightPlayerId={session.playerId}
          title="Final standings"
        />
      )}

      {mine && (
        <Card>
          <h2 className="mb-3 text-xs uppercase tracking-wide text-muted">How you did</h2>
          <dl className="grid grid-cols-3 gap-3 text-center">
            <Stat label="Right" value={mine.correct} tone="text-go" />
            <Stat label="Wrong" value={mine.wrong} tone="text-stop" />
            {/* Sat out, not got wrong -- the timeout rows are what make this honest. */}
            <Stat label="Missed" value={mine.unanswered} tone="text-muted" />
          </dl>
          {mine.avgResponseMs !== null && (
            <p className="mt-4 text-center text-sm text-muted">
              Average answer in {(mine.avgResponseMs / 1000).toFixed(1)}s
            </p>
          )}
        </Card>
      )}

      <SupportCta />

      {/*
        The way out, said out loud.
        Tapping your own name in the header still works and is still the only
        exit while a quiz is running -- deliberately quiet, because leaving
        mid-quiz on a paid night is close to irreversible. Once it has ended
        none of that applies, and the player needs this: the session pins them
        to a finished event, so without it they cannot reach the list to join
        whatever is on next.

        It sits below the crowdfunder on purpose. Above it, everybody taps this
        and nobody ever sees the campaign; below it, it is the last thing on the
        screen and the natural end of the scroll.
      */}
      <div className="mt-2">
        <Button variant="primary" onClick={onLeave}>
          Back to the quiz list
        </Button>
        <p className="mt-2 text-center text-sm text-muted">
          See what else is on tonight. Your score stays on the board.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div>
      <dd className={`text-3xl font-bold tabular-nums ${tone}`}>{value}</dd>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
    </div>
  );
}

function ordinal(n: number): string {
  if (n <= 0) return '—';
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return `${n}th`;
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th'}`;
}
